import React, { useState, useMemo } from 'react';
import {
    Box,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    TextField,
    InputAdornment
} from '@mui/material';
import { ExpandMore, List, Search } from '@mui/icons-material';
import PropTypes from 'prop-types';
import ActionButton from './ActionButton.jsx';
import { PALETTE, alpha} from '../theme';

/**
 * Shared "Select X from List" checklist used by every Travel Map section.
 *
 * Encapsulates the accordion, the search field, the Select-All / Deselect-All
 * buttons and the checkbox grid in one place so layout is identical across all
 * 24 maps. Items can carry an optional `subtitle` (e.g. parks show their state,
 * Caribbean islands show their region); the grid layout is the same regardless.
 *
 * THIS IS THE SINGLE PLACE to change checklist UI — edits here apply to every
 * map at once.
 */
const MapChecklist = ({
    items,
    visited,
    onToggle,
    onSelectAll,
    onDeselectAll,
    noun,
    hexColor,
    defaultExpanded = true,
    hoverAccent = alpha('primary', 0.05)
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [searchTerm, setSearchTerm] = useState('');

    const filtered = useMemo(() => {
        if (!searchTerm) return items;
        const q = searchTerm.toLowerCase();
        return items.filter(item =>
            item.name.toLowerCase().includes(q) ||
            (item.subtitle && item.subtitle.toLowerCase().includes(q))
        );
    }, [items, searchTerm]);

    const allSelected = visited.length === items.length;
    const noneSelected = visited.length === 0;

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            disableGutters
            elevation={0}
            sx={{
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                mb: { xs: 2, sm: 3 },
                '& .MuiAccordionSummary-root': {
                    minHeight: 'auto',
                    py: 0.5,
                    px: 0
                },
                '& .MuiAccordionDetails-root': {
                    px: 0,
                    pt: 1.5,
                    pb: 0
                }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, flexWrap: 'wrap' } }}
            >
                <List sx={{ color: PALETTE.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: PALETTE.text }}>
                    Select {noun} from List
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ color: PALETTE.textMuted, display: { xs: 'none', sm: 'block' } }}
                >
                    (Alternative to clicking on map)
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {/* Search + quick actions */}
                <Box sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <TextField
                        size="small"
                        placeholder={`Search ${noun.toLowerCase()}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: PALETTE.textMuted }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { sm: 220 },
                            // Match the ActionButton height (48px) so the input
                            // and the Select All / Deselect All buttons sit on
                            // the same baseline in the toolbar row.
                            '& .MuiOutlinedInput-root': { height: 48, borderRadius: 2 }
                        }}
                    />
                    <ActionButton
                        variant="secondary"
                        onClick={onSelectAll}
                        disabled={allSelected}
                    >
                        Select All
                    </ActionButton>
                    <ActionButton
                        variant="secondary"
                        onClick={onDeselectAll}
                        disabled={noneSelected}
                    >
                        Deselect All
                    </ActionButton>
                </Box>

                {/* Checkbox grid — consistent layout across every map */}
                <Box>
                    <Grid container spacing={1}>
                        {filtered.map((item) => {
                            const isVisited = visited.includes(item.id);
                            return (
                                <Grid size={{ xs: 6, sm: 4, md: 4 }} key={item.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isVisited}
                                                onChange={() => onToggle(item.id)}
                                                sx={{
                                                    color: PALETTE.textVeryFaint,
                                                    '&.Mui-checked': { color: hexColor }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: PALETTE.text,
                                                        fontWeight: isVisited ? 700 : 500,
                                                        fontSize: { xs: '0.85rem', sm: '1.05rem' },
                                                        lineHeight: 1.25
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                                {item.subtitle && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: PALETTE.textMuted, display: 'block', lineHeight: 1.2 }}
                                                    >
                                                        {item.subtitle}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{
                                            m: 0,
                                            // Center-align so the checkbox sits visually middle of
                                            // the label — flex-start used to leave the box hanging
                                            // above the text for single-line items.
                                            alignItems: 'center',
                                            '&:hover': {
                                                backgroundColor: hoverAccent,
                                                borderRadius: 1
                                            }
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                    {filtered.length === 0 && (
                        <Typography variant="body2" sx={{ textAlign: 'center', color: PALETTE.textMuted, mt: 2 }}>
                            No matches for &ldquo;{searchTerm}&rdquo;.
                        </Typography>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

MapChecklist.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        subtitle: PropTypes.string
    })).isRequired,
    visited: PropTypes.array.isRequired,
    onToggle: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onDeselectAll: PropTypes.func.isRequired,
    noun: PropTypes.string.isRequired,
    hexColor: PropTypes.string.isRequired,
    defaultExpanded: PropTypes.bool,
    hoverAccent: PropTypes.string
};

export default MapChecklist;
